/**
 * TradeTask + ProjectTradeTask entity DTOs.
 *
 * Mirrors the Prisma `TradeTask` catalogue and the per-project
 * `ProjectTradeTask` link rows. Used by the mobile Methods screen
 * (Project → Methods sub-screen, mirrors web at
 * `app/(app)/projects/[projectId]/methods/`).
 *
 * Web wire shape is set-based (POST replaces the whole list). The mobile
 * contract exposes the same set-replace semantics on POST and an
 * additive `add` request for parity with other mobile resources.
 */
import { z } from "zod";
import {
  INT32_MAX,
  INT32_MIN,
  entityIdSchema,
  orderIndexSchema,
} from "../common";

/**
 * Catalogue trade task (Prisma TradeTask). Read-only over the API.
 */
export const TradeTaskSchema = z.object({
  id: z.string().cuid(),
  slug: z.string(),
  tradeName: z.string(),
  taskName: z.string(),
  description: z.string().nullable(),
  defaultMethodSteps: z.string(),
  orderIndex: z.number().int().min(INT32_MIN).max(INT32_MAX),
  associatedHazardSlugs: z.array(z.string()),
  createdAt: z.string().datetime(),
});

export type TradeTask = z.infer<typeof TradeTaskSchema>;

// #2411 server-boundary variant.
export const TradeTaskStrict = TradeTaskSchema.strict();

/**
 * Per-project method statement record (Prisma ProjectTradeTask). Surfaces
 * the project's order + custom notes; the underlying catalogue trade task
 * is nested as `tradeTask` for convenience (saves a round trip on the
 * list screen).
 */
export const ProjectTradeTaskRecordSchema = z.object({
  id: z.string().cuid(),
  projectId: entityIdSchema,
  tradeTaskId: z.string().cuid(),
  customNotes: z.string().nullable(),
  orderIndex: z.number().int().min(INT32_MIN).max(INT32_MAX),
  createdAt: z.string().datetime(),
  tradeTask: TradeTaskSchema.optional(),
});

// #2411 server-boundary variant — full depth, including the nested catalogue
// task the mobile app parses permissively.
export const ProjectTradeTaskRecordStrict =
  ProjectTradeTaskRecordSchema.extend({
    tradeTask: TradeTaskStrict.optional(),
  }).strict();

export type ProjectTradeTaskRecord = z.infer<
  typeof ProjectTradeTaskRecordSchema
>;

/**
 * POST /api/mobile/v1/projects/:projectId/trade-tasks body — set-replace
 * semantics. Server deletes existing project links and re-creates from
 * this list. Mirrors the web POST behaviour exactly so the same backend
 * can serve both.
 */
export const ProjectTradeTasksReplaceRequest = z
  .object({
    tasks: z.array(
      z
        .object({
          tradeTaskId: z.string().cuid(),
          customNotes: z.string().nullable().optional(),
          orderIndex: orderIndexSchema,
        })
        .strict(),
    ),
  })
  .strict();

export type ProjectTradeTasksReplaceRequest = z.infer<
  typeof ProjectTradeTasksReplaceRequest
>;

/**
 * PATCH /api/mobile/v1/projects/:projectId/trade-tasks/:taskId body — only
 * notes + order can be edited; the underlying catalogue task is immutable.
 */
export const ProjectTradeTaskUpdateRequest = z
  .object({
    customNotes: z.string().nullable().optional(),
    orderIndex: orderIndexSchema.optional(),
  })
  .strict();

export type ProjectTradeTaskUpdateRequest = z.infer<
  typeof ProjectTradeTaskUpdateRequest
>;
